import { Input } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
  CM_IN_FOOT,
  cmToInch,
  FOOT_IN_INCHES,
  inchesToCm,
  kgToPounds,
  poundsToKg
} from "src/lib/unitConversion";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import { parseNumberInput } from "src/ui/components/HeightInput/HeightInput";
import { FormErrorContext } from "../QuestionnaireStep/FormErrorContext";
import { findPairedWeightFieldId } from "./BodyMetricsInput.helpers";

const Fieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;

  & + & {
    margin-top: 1.5em;
  }
`;

const Legend = styled.legend`
  margin: 0 0 0.5em;
  padding: 0;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--Greys-Charcoal80, rgba(33, 33, 33, 0.8));
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 1em;
`;

const ErrorMessage = styled.div`
  padding: 0.5em 1em 1em;
  display: block;
  color: var(--primary-warning-red, #bf1902);
  font-size: 0.875rem;

  @keyframes show {
    from {
      opacity: 0;
      transform: translateY(-0.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: show 0.2s linear forwards;
`;

/**
 * Renders height (feet/inches) and weight (pounds) together on one screen,
 * but saves them as two real answers so each stays a plain number: height
 * under this field's own id, weight under the id of the hidden
 * `[only-logic]` question this field is paired with via
 * `[weight-field-ref="<ref>"]` (see BodyMetricsInput.helpers). Routed to by
 * `field.properties.body_metrics_field` in DynamicInput.
 */
const BodyMetricsInput: FC<{
  field: QuestionnaireField;
}> = (props) => {
  const heightFieldId = props.field.id;
  const { getValues, setValue } = useFormContext();
  const [{ fields }, { saveValue }] = useQuestionnaireCubitInContext();
  const { setFieldError, clearFieldError } = React.useContext(FormErrorContext);
  const [heightError, setHeightError] = useState<string | undefined>(undefined);
  const [weightError, setWeightError] = useState<string | undefined>(undefined);

  const weightFieldId = findPairedWeightFieldId(
    fields,
    props.field.properties?.weight_field_ref
  );

  useEffect(() => {
    if (!weightFieldId) {
      reportErrorSentry(
        new Error(
          "BodyMetricsInput: no paired weight field found for weight_field_ref"
        ),
        { extra: { fieldId: heightFieldId } }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report once per mount, not on every fields/weightFieldId identity change
  }, []);

  const heightCm = Number(getValues(heightFieldId) ?? 0);
  const weightKg = weightFieldId ? Number(getValues(weightFieldId) ?? 0) : 0;

  const inInches = useMemo(() => cmToInch(heightCm), [heightCm]);
  const inPounds = useMemo(() => kgToPounds(weightKg), [weightKg]);

  const ft = useRef(Math.floor(inInches / FOOT_IN_INCHES));
  const inches = useRef(Math.round(inInches % FOOT_IN_INCHES));
  const pounds = useRef(Math.round(inPounds));

  const commitValue = (): void => {
    clearFieldError(heightFieldId);

    const heightCmValue = inchesToCm(
      ft.current * FOOT_IN_INCHES + inches.current
    );
    const isHeightValid = heightCmValue >= CM_IN_FOOT && !isNaN(heightCmValue);
    const heightErrorMsg =
      isHeightValid && (heightCmValue < 50 || heightCmValue > 300)
        ? translate("error_validate_height_invalid")
        : undefined;

    const weightKgValue = poundsToKg(pounds.current);
    const isWeightValid = pounds.current > 0 && !isNaN(weightKgValue);
    const weightErrorMsg =
      isWeightValid && (weightKgValue < 2 || weightKgValue > 300)
        ? translate("error_validate_weight_invalid")
        : undefined;

    setHeightError(heightErrorMsg);
    setWeightError(weightErrorMsg);

    if (heightErrorMsg ?? weightErrorMsg) {
      setFieldError(heightFieldId, heightErrorMsg ?? weightErrorMsg ?? "");
    }

    const heightValue = isHeightValid ? heightCmValue : undefined;
    saveValue(heightFieldId, heightValue);
    setValue(heightFieldId, heightValue);

    if (weightFieldId) {
      const weightValue = isWeightValid ? weightKgValue : undefined;
      saveValue(weightFieldId, weightValue);
      setValue(weightFieldId, weightValue);
    }
  };

  const handleFeetChange = (newFtString: string): void => {
    ft.current = parseNumberInput(newFtString);
    commitValue();
  };

  const handleInchesChange = (newInchesString: string): void => {
    inches.current = parseNumberInput(newInchesString);
    commitValue();
  };

  const handlePoundsChange = (newPoundsString: string): void => {
    pounds.current = parseNumberInput(newPoundsString);
    commitValue();
  };

  useEffect(() => {
    commitValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when the prefilled cm/kg answer changes, not on every render
  }, [inInches, inPounds]);

  const initialValues = {
    ft: ft.current ? `${ft.current}` : "",
    inch: inches.current ? `${inches.current}` : "",
    lbs: pounds.current ? `${pounds.current}` : ""
  };

  return (
    <div>
      <Fieldset>
        <Legend>{translate("bodyMetrics.height.label")}</Legend>
        <Grid>
          <Input
            name="feet"
            defaultValue={initialValues.ft}
            placeholder={translate("feet")}
            label={translate("feet")}
            isRequired
            maxLength={1}
            pattern="[0-9]*"
            mask="0"
            inputMode="numeric"
            aria-label={translate("feet")}
            icon={translate("units.feet")}
            onChange={(e) => handleFeetChange(e)}
          />
          <Input
            name="inches"
            defaultValue={initialValues.inch}
            placeholder={translate("inches")}
            label={translate("inches")}
            maxLength={2}
            mask="0[0]"
            pattern="[0-9]*"
            inputMode="numeric"
            aria-label={translate("inches")}
            icon={translate("units.inches")}
            onChange={(e) => handleInchesChange(e)}
          />
        </Grid>
        {heightError && (
          <ErrorMessage className="little1">{heightError}</ErrorMessage>
        )}
      </Fieldset>
      <Fieldset>
        <Legend>{translate("bodyMetrics.weight.label")}</Legend>
        <Input
          name="weight"
          defaultValue={initialValues.lbs}
          placeholder={translate("pounds")}
          label={translate("pounds")}
          isRequired
          maxLength={3}
          minLength={1}
          pattern="[0-9]*"
          mask="0[0][0]"
          inputMode="numeric"
          aria-label={translate("pounds")}
          icon={translate("units.pounds")}
          onChange={(e) => handlePoundsChange(e)}
          error={weightError}
        />
      </Fieldset>
    </div>
  );
};

export default BodyMetricsInput;
